import { useRef, useState, useEffect, useCallback } from 'react';
import { usePageLinkProps } from './usePageLinkProps.js';
import { usePaginationContext } from './usePaginationContext.js';
import '../../LinkProvider/LinkProvider.js';
import '@emotion/react';
import '../PaginationContext.js';

const usePaginationGoTo = () => {
  const inputRef = useRef(null);
  const [showInvalidState, setShowInvalidState] = useState(false);
  const paginationContext = usePaginationContext();
  const {
    activePage,
    totalPages,
    handlePageChange,
    hasPaginationList,
    orientation,
    setPageLinkProps
  } = paginationContext ?? {};
  const [inputValue, setInputValue] = useState(`${activePage ?? 1}`);
  const previousActivePageRef = useRef(activePage);
  useEffect(() => {
    if (activePage !== previousActivePageRef.current) {
      previousActivePageRef.current = activePage;
      const activePageStr = `${activePage}`;
      if (inputValue !== activePageStr) {
        setInputValue(activePageStr);
      }
    }
  }, [activePage, inputValue]);
  const [validInputNum, setValidInputNum] = useState(activePage);
  const handleInputChange = useCallback(e => {
    setInputValue(e.target.value);
    const pgNum = parseInt(e.target.value, 10);
    if (pgNum && totalPages !== undefined && pgNum <= totalPages && pgNum > 0) {
      if (showInvalidState) setShowInvalidState(false);
      if (validInputNum !== pgNum) setValidInputNum(pgNum);
    } else if (typeof validInputNum === "number") {
      setValidInputNum(undefined);
    }
  }, [totalPages, validInputNum, showInvalidState]);
  const handleSubmit = useCallback(e => {
    if (e) e.preventDefault();
    if (inputRef.current) {
      const pgNum = parseInt(inputRef.current.value, 10);
      if (!pgNum || totalPages === undefined || pgNum > totalPages || pgNum < 0) {
        setShowInvalidState(true);
        inputRef.current.focus();
      } else if (pgNum && pgNum <= totalPages && handlePageChange) {
        handlePageChange(pgNum);
      }
    }
  }, [handlePageChange, totalPages]);
  const handleInvalidLinkSubmit = useCallback(() => {
    if (!showInvalidState) setShowInvalidState(true);
    if (inputRef.current) inputRef.current.focus();
  }, [showInvalidState]);
  const goBtnRef = useRef(null);
  const goLinkRef = useRef(null);
  const handlePrevClick = useCallback(willBecomeDisabled => {
    if (willBecomeDisabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  const handleNextClick = useCallback(willBecomeDisabled => {
    const el = goLinkRef.current ?? goBtnRef.current;
    if (willBecomeDisabled && el) {
      el.focus();
    }
  }, []);
  const pageLinkProps = usePageLinkProps({
    pageNumber: validInputNum ?? null,
    onClick: handleSubmit
  });
  const shouldRenderLink = !!setPageLinkProps;
  return {
    paginationContext,
    hasPaginationList: !!hasPaginationList,
    orientation,
    handleSubmit,
    inputRef,
    handleInputChange,
    showInvalidState,
    handleInvalidLinkSubmit,
    validInputNum,
    goBtnRef,
    goLinkRef,
    handlePrevClick,
    handleNextClick,
    pageLinkProps,
    shouldRenderLink,
    inputValue
  };
};

export { usePaginationGoTo };
